import type { ApolloError } from '@apollo/client';
import { faRefresh } from '@fortawesome/pro-regular-svg-icons';
import {
  Button,
  Heading,
  LineChart,
  Spinner,
  Surface,
  Text,
} from '@quality24/design-system';
import classNames from 'classnames';
import * as React from 'react';

import type { AllowedValue } from '@nivo/line';
import { merge } from 'lodash';
import {
  DateFilter,
  LineChartComponentData,
  ValueFormatter,
} from '../../../../@types';

import { filterData } from '../../utils/filter';
import { mapData } from '../../utils/mapper';
import { createReferenceMap } from '../../utils/reference';
import { formatChartData } from './utils';

import { formatValue, parseDataValue } from '../../../../utils';
import styles from '../../DataViewContainer.module.scss';

export interface Props extends React.HTMLAttributes<HTMLElement> {
  loading?: boolean;
  error?: ApolloError;
  refetch: () => void;
  data: Record<string, unknown>;
  dateFilter: DateFilter;
  renderOnSurface?: boolean;
  config: Omit<LineChartComponentData, 'type'>;
}

/**
 * <LineChartContainer> page
 */
export const LineChartContainer: React.FunctionComponent<Props> = ({
  className,
  loading,
  error,
  refetch,
  data,
  dateFilter,
  renderOnSurface = true,
  config: {
    title,
    className: configClassName,
    options,
    displayLegend,
    postQuery,
    rootKey,
    aggrKey,
    references,
    groupBy,
    surfaceClassName,
  },
  ...htmlElementProps
}) => {
  /**
   * Gets the aggregation data for this container
   */
  const aggrData = React.useMemo(
    () =>
      filterData(data, rootKey, postQuery) as Record<string, string | number>[],
    [data, rootKey, postQuery],
  );

  /**
   * Creates the reference map
   */
  const referenceMap = React.useMemo(
    () => createReferenceMap({ data, references, dateFilter, postQuery }),
    [data, references, dateFilter, postQuery],
  );

  /**
   * Maps data
   */
  const mappedData = React.useMemo(
    () =>
      mapData({
        data: aggrData,
        groupBy,
        aggrKey,
        reference: referenceMap,
      }) as Array<Record<string, string | number>>,
    [aggrData, groupBy, aggrKey, referenceMap],
  );

  /**
   * Post-processing - Calculate percentages
   */
  const chartData = React.useMemo(
    () => formatChartData(aggrKey, mappedData),
    [aggrKey, mappedData],
  );

  // Add log for debuging with DVC-Designer
  if (process.env.REACT_APP_HOST_PROJECT === 'designer') {
    // eslint-disable-next-line no-console
    console.info('LineChartContainer', {
      originalData: data,
      aggregatedData: aggrData,
      referenceMap,
      mappedData,
      chartData,
    });
  }

  const processedOptions = React.useMemo(() => {
    const getFormatterFunction = (formatter: ValueFormatter) => {
      return (value: AllowedValue) => {
        if (['string', 'number'].includes(typeof value)) {
          const result = formatValue(value as string | number, formatter);

          if (typeof result === 'number') {
            return result.toString();
          }

          return result;
        }

        if (value instanceof Date) {
          return value.toLocaleDateString();
        }

        return value?.toString() || '';
      };
    };
    const newOptions = { ...options };

    if (options?.xCustomFormatter) {
      newOptions.xFormat = getFormatterFunction(options.xCustomFormatter);
    }

    if (options?.yCustomFormatter) {
      newOptions.yFormat = getFormatterFunction(options.yCustomFormatter);
    }

    if (options?.axisLeftCustomFormatter) {
      newOptions.axisLeft = merge(newOptions.axisLeft, {
        format: getFormatterFunction(options.axisLeftCustomFormatter),
      });
    }

    if (options?.axisBottomCustomFormatter) {
      newOptions.axisBottom = merge(newOptions.axisBottom, {
        format: getFormatterFunction(options.axisBottomCustomFormatter),
      });
    }

    if (options?.axisRightCustomFormatter) {
      newOptions.axisRight = merge(newOptions.axisRight, {
        format: getFormatterFunction(options.axisRightCustomFormatter),
      });
    }

    if (options?.axisTopCustomFormatter) {
      newOptions.axisTop = merge(newOptions.axisTop, {
        format: getFormatterFunction(options.axisTopCustomFormatter),
      });
    }

    return newOptions;
  }, [options]);

  return (
    <div
      className={classNames(className, configClassName)}
      {...htmlElementProps}
    >
      <Surface
        className={classNames(styles.surface, surfaceClassName, {
          'on-surface': renderOnSurface,
        })}
        padding="sm"
        backgroundColor="grayLight2"
      >
        {title && (
          <Heading as="h2" size="sm2" weight="bold" className="mb-4">
            {!loading && parseDataValue(data, title, 'string')}
          </Heading>
        )}

        {!loading && !error && aggrData.length === 0 && (
          <Text size="sm2" color="gray100">
            Não há dados disponíveis.
          </Text>
        )}

        {!loading && error && (
          <div className={styles['error-container']}>
            <Text className="mb-2" size="sm2" color="gray100">
              Não foi possível carregar os dados.
            </Text>
            <Button
              variant="outline"
              icon={faRefresh}
              onClick={() => refetch()}
            >
              Recarregar
            </Button>
          </div>
        )}

        {loading && (
          <div className="p-3 d-flex flex-justify-center">
            <Spinner />
          </div>
        )}

        {!error && aggrData.length > 0 && (
          <LineChart
            data={chartData}
            options={processedOptions}
            displayLegend={displayLegend !== false}
          />
        )}
      </Surface>
    </div>
  );
};

export default LineChartContainer;
